import * as React from "react";
import { CalendarDays } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// The calendar (react-day-picker + date-fns, ~78 kB minified) is code-split: it is only needed once
// a picker is opened, so it stays out of the main chunk. `preloadCalendar` warms it on hover/focus
// of a trigger so the popover normally opens with the grid already loaded.
const loadCalendar = () => import("@/components/ui/calendar");
const Calendar = React.lazy(() =>
  loadCalendar().then((m) => ({ default: m.Calendar })),
);
function preloadCalendar(): void {
  void loadCalendar();
}

// ─── Local-date helpers ───────────────────────────────────────────────────────
//
// The picker's value contract is the exact string a native `<input type="date">` produces:
// 'YYYY-MM-DD', or '' when empty. These helpers convert between that string and a LOCAL-midnight
// Date. Never `new Date('YYYY-MM-DD')`: the spec parses a date-only ISO string as UTC midnight, so
// anywhere west of UTC it lands on the previous calendar day (the classic off-by-one).

const DATE_VALUE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 'YYYY-MM-DD' → local-midnight Date, or undefined when empty/malformed/impossible (e.g. 02-30). */
export function parseDateValue(
  value: string | null | undefined,
): Date | undefined {
  if (!value) return undefined;
  const match = DATE_VALUE_RE.exec(value);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);
  // Reject values the Date constructor silently rolls over (2026-02-30 → 2026-03-02).
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return undefined;
  }
  return date;
}

/** Date → 'YYYY-MM-DD' from its LOCAL calendar fields. */
export function toDateValue(date: Date): string {
  const y = String(date.getFullYear()).padStart(4, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Built per call rather than once at module load: an Intl formatter pins the host time zone at
// construction, and the Date it formats is local-midnight in the CURRENT zone — a stale formatter
// could render the neighbouring day. Construction cost is negligible at one call per render.
function formatLocal(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

/** 'YYYY-MM-DD' → locale display string (e.g. "Oct 8, 2026"), or '' when empty/invalid. */
export function formatDateDisplay(value: string | null | undefined): string {
  const date = parseDateValue(value);
  return date ? formatLocal(date) : "";
}

function addYears(date: Date, years: number): Date {
  return new Date(date.getFullYear() + years, date.getMonth(), 1);
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface DatePickerProps {
  /** Put on the trigger button, so an existing `<Label htmlFor>` keeps labelling the control. */
  id?: string;
  /** 'YYYY-MM-DD', or '' / undefined when empty — same contract as a native date input. */
  value: string | null | undefined;
  /** Called with 'YYYY-MM-DD', or '' when cleared. */
  onChange: (value: string) => void;
  /** Called when the calendar closes — wire to react-hook-form's `field.onBlur`. */
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** Earliest selectable day, 'YYYY-MM-DD' (inclusive) — same semantics as the native `min`. */
  min?: string;
  /** Latest selectable day, 'YYYY-MM-DD' (inclusive) — same semantics as the native `max`. */
  max?: string;
  /** Error message: sets aria-invalid, renders the message, and links it via aria-describedby. */
  error?: string;
  /** Renders a hidden input carrying the value, for native form submission. */
  name?: string;
  required?: boolean;
  /**
   * Show a "Clear" action in the calendar. Defaults to `!required` — an optional native date input
   * can always be emptied, so an optional picker must be too.
   */
  clearable?: boolean;
  className?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  /** Extra description ids (e.g. an externally rendered error/hint) — merged with our own. */
  "aria-describedby"?: string;
  "data-testid"?: string;
}

/**
 * DatePicker — accessible, design-system replacement for `<input type="date">`.
 *
 * A trigger button styled exactly like `Input` opens a Radix Popover holding the Calendar. Radix
 * provides the focus management (focus moves into the calendar on open, Esc closes and returns
 * focus to the trigger, outside click dismisses); react-day-picker provides the grid keyboard model
 * (arrows move by day/week, PageUp/PageDown by month, Home/End to week bounds, Enter/Space select).
 *
 * The ref is forwarded to the trigger so react-hook-form's `shouldFocusError` can focus it.
 */
export const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      id,
      value,
      onChange,
      onBlur,
      placeholder = "Select a date",
      disabled,
      min,
      max,
      error,
      name,
      required,
      clearable,
      className,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy,
      "data-testid": testId,
    },
    ref,
  ) => {
    const autoId = React.useId();
    const triggerId = id ?? autoId;
    const valueId = `${triggerId}-value`;
    const errorId = `${triggerId}-error`;
    const [open, setOpen] = React.useState(false);

    const selected = parseDateValue(value);
    const minDate = parseDateValue(min);
    const maxDate = parseDateValue(max);
    const display = selected ? formatLocal(selected) : "";
    const canClear = (clearable ?? !required) && !!selected;

    // Open on the selected month; otherwise today, clamped into [min, max] so the user never lands
    // on a month where every day is disabled.
    // (Only read when the calendar mounts, i.e. on each open — no memo needed.)
    const defaultMonth = ((): Date => {
      if (selected) return selected;
      const today = new Date();
      if (minDate && today < minDate) return minDate;
      if (maxDate && today > maxDate) return maxDate;
      return today;
    })();

    const disabledDays = [
      ...(minDate ? [{ before: minDate }] : []),
      ...(maxDate ? [{ after: maxDate }] : []),
    ];

    const handleOpenChange = (next: boolean): void => {
      setOpen(next);
      if (!next) onBlur?.();
    };

    const describedBy =
      [
        ariaDescribedBy,
        display ? valueId : undefined,
        error ? errorId : undefined,
      ]
        .filter(Boolean)
        .join(" ") || undefined;

    return (
      <div className="flex flex-col gap-1.5">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              ref={ref}
              id={triggerId}
              type="button"
              disabled={disabled}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
              aria-describedby={describedBy}
              aria-invalid={error ? true : undefined}
              aria-required={required || undefined}
              data-testid={testId}
              data-empty={!display || undefined}
              onPointerEnter={preloadCalendar}
              onFocus={preloadCalendar}
              className={cn(
                // Mirrors `Input`'s classes so the two controls sit flush in the same form row.
                "flex h-[42px] w-full min-w-0 items-center justify-between gap-2 rounded-[var(--radius-input)] border border-outlines bg-white px-3.5 py-2.5 text-left text-sm text-text-primary",
                "focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary",
                "disabled:pointer-events-none disabled:opacity-50",
                error && "border-error",
                className,
              )}
            >
              <span
                className={cn("truncate", !display && "text-text-placeholder")}
              >
                {display || placeholder}
              </span>
              <CalendarDays
                className="h-4 w-4 shrink-0 text-text-secondary"
                aria-hidden="true"
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-auto p-0"
            // Let react-day-picker's `autoFocus` put focus on the selected day (or today / the
            // first enabled day) instead of Radix focusing the first focusable (prev-month button).
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            <React.Suspense
              fallback={
                <div
                  role="status"
                  className="flex h-[19rem] w-[17rem] items-center justify-center text-sm text-text-secondary"
                >
                  Loading calendar…
                </div>
              }
            >
              <Calendar
                mode="single"
                // `required` stops a click on the already-selected day from de-selecting it; clearing
                // is the explicit "Clear" action below.
                required
                selected={selected}
                onSelect={(date: Date) => {
                  onChange(toDateValue(date));
                  handleOpenChange(false);
                }}
                defaultMonth={defaultMonth}
                captionLayout="dropdown"
                // Explicit bounds: the dropdown otherwise stops at the end of the CURRENT year, which
                // would make future planned dates unreachable.
                startMonth={minDate ?? addYears(defaultMonth, -10)}
                endMonth={maxDate ?? addYears(defaultMonth, 10)}
                disabled={disabledDays.length ? disabledDays : undefined}
                autoFocus
              />
            </React.Suspense>
            {canClear && (
              <div className="flex justify-end border-t border-outlines px-3 py-2">
                <button
                  type="button"
                  className="rounded-[var(--radius-button)] px-2 py-1 text-sm font-medium text-primary hover:bg-primary-subtle focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                  onClick={() => {
                    onChange("");
                    handleOpenChange(false);
                  }}
                >
                  Clear
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        {/* A <label htmlFor> names the button, which replaces its text content as the accessible
            name — so the chosen date is exposed as the description instead. */}
        {display && (
          <span id={valueId} className="sr-only">
            {`Selected date: ${display}`}
          </span>
        )}
        {name && <input type="hidden" name={name} value={value ?? ""} />}
        {error && (
          <p id={errorId} role="alert" className="text-xs text-error">
            {error}
          </p>
        )}
      </div>
    );
  },
);
DatePicker.displayName = "DatePicker";
